import { useMemo, useEffect } from 'react'
import { getUserFinancialAccounts } from '@/selectors/financeSelectors'
import { useReselector } from '@/utils/sharedHooks'
import { Card, CardColors, Separator, Text } from '@/ui'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { selectAdminAnnualTaxFilingsByYearForUser } from '@/features/admin/taxes/adminAnnualTaxFilings.selector'
import { useAppDispatch, filterNulls } from '@/utils/typeHelpers'
import { fetchAdminAnnualTaxFilings } from '@/features/admin/taxes/adminAnnualTaxFilings.slice'
import { FinancialAccountWithAdminInfo } from '@/reducers/admin/financialAccountsReducer'
import { sortBy, startCase } from 'lodash'
import { DateTime } from 'luxon'
import { TypographyStyles } from '@/styles'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'

const questionMap: {
  [identifier: string]: {
    text: string
    order: number
  }
} = {
  isBusinessAccount: {
    text: 'Is this a business account?',
    order: 0,
  },
  hasPersonalTransactions: {
    text: 'Does it have any personal transactions?',
    order: 1,
  },
  hasBusinessTransactions: {
    text: 'Does it have any business transactions?',
    order: 2,
  },
  isAccountOpenForYear: {
    text: 'Has this account been open for the entire year?',
    order: 3,
  },
}

const PrimaryUseAnswer = ({
  account,
}: {
  account: FinancialAccountWithAdminInfo
}) => {
  const { primaryUse, primaryUseSetAt } = account

  return (
    <div className="w-2/3 mt-2">
      <Text variant={TypographyStyles.H3} className="w-full">
        Declared primary use
      </Text>
      <Text variant={TypographyStyles.BODY_MD} className="w-full">
        {primaryUse
          ? `${startCase(primaryUse)}${
              primaryUseSetAt
                ? ` — ${DateTime.fromISO(primaryUseSetAt).toFormat(
                    DATE_FORMATS_LUXON.DISPLAY_SHORT
                  )}`
                : ''
            }`
          : 'Not asked'}
      </Text>
    </div>
  )
}

export const AccountAnswersCard = ({
  account,
  index,
}: {
  account: FinancialAccountWithAdminInfo
  index: number
}) => {
  const questionArray = useMemo(() => {
    const { onboardingAnswers } = account
    const unsortedList = Object.keys(onboardingAnswers).map((key) => ({
      text: questionMap[key].text,
      value: onboardingAnswers[key],
      order: questionMap[key].order,
      key,
    }))
    return sortBy(unsortedList, 'order')
  }, [account])

  return (
    <Card color={CardColors.LEVEL_1}>
      <div className="flex flex-col gap-2">
        <Text variant={TypographyStyles.EYEBROW} className="w-full">
          CONNECTED ACCOUNT {index + 1}
        </Text>
        <Text variant={TypographyStyles.BODY_MD} className="w-full">
          <b> {account.name} </b>
        </Text>
        <PrimaryUseAnswer account={account} />
        {questionArray.map((question) => {
          return (
            <div key={question.key} className="w-2/3 mt-2">
              <Text variant={TypographyStyles.H3} className="w-full">
                {question.text}
              </Text>
              <Text variant={TypographyStyles.BODY_MD} className="w-full">
                {question.value ? 'Yes' : 'No'}
              </Text>
            </div>
          )
        })}
      </div>
    </Card>
  )
}

export const OnboardingAnswers = ({ userId }: { userId: number }) => {
  const dispatch = useAppDispatch()
  const accounts = useReselector(getUserFinancialAccounts, userId)
  const taxYear = useReselector(selectCurrentAnnualTaxYear)

  const annualTaxFiling = useReselector(
    selectAdminAnnualTaxFilingsByYearForUser,
    taxYear,
    userId
  )

  useEffect(() => {
    fetchAdminAnnualTaxFilings({
      page: 1,
      limit: 1,
      year: taxYear,
      userIds: userId,
    })(dispatch)
  }, [dispatch, taxYear, userId])

  const accountList = useMemo(() => {
    if (!accounts) return []
    const accountArr = filterNulls(Object.values(accounts))
    return accountArr.filter((account) => account?.onboardingAnswers)
  }, [accounts])

  const needsAnnualTaxes = useMemo(() => {
    if (!annualTaxFiling) return 'No'
    const { optedOutAt, pretaxSurveySubmittedAt, isLateJoiner } =
      annualTaxFiling
    if (optedOutAt) return 'No'
    if (pretaxSurveySubmittedAt || isLateJoiner) return 'Yes'
    return 'No'
  }, [annualTaxFiling])

  return (
    <Card className="w-full">
      <div className="flex flex-col gap-2">
        <Text variant={TypographyStyles.H2} className="w-full">
          {' '}
          Sign Up Questions{' '}
        </Text>
        <br />
        <Text variant={TypographyStyles.BODY_LG} className="w-full">
          {' '}
          Would you like Heard to prepare your {taxYear} taxes?{' '}
        </Text>
        <Text variant={TypographyStyles.BODY_LG} className="w-full">
          {' '}
          {needsAnnualTaxes}{' '}
        </Text>
        <Separator />
        <div className="flex flex-col gap-2">
          {accountList.map((account, index) => (
            <AccountAnswersCard
              key={account.id}
              account={account}
              index={index}
            />
          ))}
        </div>
      </div>
    </Card>
  )
}
